import type {GqlOperation} from "@khanacademy/wonder-blocks-data";
import {khanFetch} from "@khan/data-fetch";

import {getGqlUri} from "./get-gql-uri.ts";
import type {GqlContextRecord} from "./types.ts";

/**
 * Fetch a GraphQL request.
 *
 * This conforms to the Wonder Blocks Data API for use with its `GqlRouter` and
 * `useGql` hook. Rather than calling this directly, it should be called via
 * those mechanisms as that allows for default context to be set easily and
 * provides a consistent API for the caller. It also simplifies story/fixture,
 * and test mocking in many cases.
 */
export const wbdGqlFetch = <TData, TVariables extends Record<any, any>>(
    operation: GqlOperation<TData, TVariables>,
    variables: TVariables | null | undefined,
    context: GqlContextRecord,
): Promise<Response> => {
    // Get the URI for making this request.
    const uri = getGqlUri(operation, context, variables);

    // Now we need to build the request options.
    // The only time we do a GET is if we are using Fastly caching and
    // the endpoint supports GET. It just so happens that we also only add
    // the operation hash in this circumstance (see getGqlUri), so let's
    // use that to determine our request method.
    const isGet = uri.searchParams.get("hash") != null;

    // If we're using GET, we have everything we need to build the request.
    if (isGet) {
        return khanFetch(uri.href);
    }

    // We're using POST, so we need to build the request options a little more.
    const requestOptions = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            operationName: operation.id,
            query: operation.query,
            variables,
        }),
    } as const;

    // TODO(somewhatabstract, FEI-4320): we probably should share in-flight
    // requests.
    // TODO(somewhatabstract, FEI-4321): Also, we want to support passing an
    // abort signal in the request options.
    return khanFetch(uri.href, requestOptions);
};
